import React, { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ChevronDown, ChevronUp, Info } from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useToast } from "@/components/ui/use-toast";
import { FormProvider, useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Separator } from "@/components/ui/separator";
import { ErrorType } from "@/types";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { TooltipProvider } from "@/components/ui/tooltip";
import { MinimalTiptapEditor } from "@/components/ui/minimal-tiptap";
import { Content } from "@tiptap/react";
import { EmailTrigger } from "@/types/system-setting/system-config";
import { useUpdateEmailTriggerMutation } from "@/services/systemConfigApi";
interface MembershipExpiryFormProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  action: string;
  setAction: React.Dispatch<React.SetStateAction<"add" | "edit">>;
  refetch: () => void;
  editModeData: EmailTrigger | undefined;
}
const MembershipExpiryForm = ({
  isOpen,
  setOpen,
  action,
  setAction,
  editModeData,
  refetch,
}: MembershipExpiryFormProps) => {
  // text editor
  const [textEditorValue, setTextEditoralue] = useState<Content>("");
  const { toast } = useToast();
  const [updateEmailTrigger, { isLoading, error, status }] = useUpdateEmailTriggerMutation();

  const initialValues = {
    trigger_name: "",
    is_notification_enabled: false,
    notification_title: "",
    notification_content: "",
    no_of_days_before: undefined,
    is_email_enabled: false,
    email_subject: "",
    email_body: "",
    is_active: false,
    key: "",
  };



  const form = useForm<EmailTrigger>({
    mode: "all",
    defaultValues: initialValues,
  });

  const {
    control,
    watch,
    register,
    handleSubmit,
    clearErrors,
    getValues,
    reset,
    setValue,
    formState: { isSubmitting, errors },
  } = form;
  const watcher = watch();

  useEffect(() => {
    if (action === "edit" && editModeData) {
      const payload = {
        ...editModeData, notification_title: "",
        notification_content: "",
      }
      reset(payload);
      setTextEditoralue(editModeData.email_body);
    } else {
      reset(initialValues);
      setTextEditoralue("");
    }
  }, [action, editModeData]);

  const onSubmit = async (data: EmailTrigger) => {
    try {
      if (action === "edit" && editModeData?.id) {
        const payload = {
          ...data,
          id: editModeData.id,
          email_body: textEditorValue as string,
        };

        const resp = await updateEmailTrigger(payload).unwrap();
        if (resp) {
          toast({
            variant: "success",
            title: "Email trigger updated successfully",
          });
          await refetch();
          handleClose();
        }
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `${typedError.data?.detail || typedError.data?.message}`,
        });
        return false;
      } else {
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `Something Went Wrong.`,
        });
        return false;
      }
    }
  };

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const handleClose = () => {
    clearErrors();
    setAction("add");
    reset(
      initialValues,
      {
        keepIsSubmitted: false, // Reset the form's submission state
        keepSubmitCount: false, // Reset the count of submissions
        keepDirtyValues: false, // Clear any dirty values (set to false to reset all fields)
        keepDefaultValues: true, // Keep default values as provided by initialValue
      }
    );

    setOpen(false);
  };

  const copyToClipboard = async (text: string) => {
    const isSecure = window.isSecureContext;

    if (isSecure && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        console.log("✅ Used Clipboard API");
        return true;
      } catch (err) {
        console.warn("❌ Clipboard API failed, using fallback", err);
      }
    }

    // fallback
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed"; // avoid scroll jump
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const success = document.execCommand("copy");
      document.body.removeChild(textarea);
      console.log("✅ Used fallback (execCommand)");
      return success;
    } catch (e) {
      console.error("❌ Both Clipboard API and fallback failed", e);
      return false;
    }
  };


  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        hideCloseButton
        className="bg-formbg w-full lg:!max-w-[1100px] p-0 custom-scrollbar h-full "
      >
        <FormProvider {...form}>
          <form
            key={action}
            noValidate
            className="pb-4 "
            onSubmit={handleSubmit(onSubmit, onError)}
          >
            <SheetHeader className="px-6 sticky top-0 z-40 py-3 bg-white">
              <SheetTitle>
                <div className="flex justify-between flex-col sm:flex-row gap-3 sm:gap-5 items-start bg-white">
                  <div>
                    <p className="font-semibold">System Configuration</p>
                    <div className="text-sm">
                      <span className="text-gray-400 pr-1 font-semibold">
                        System Configuration
                      </span>{" "}
                      <span className="text-gray-400 font-semibold">/</span>
                      <span className="pl-1 text-primary font-semibold ">
                        {action == "add" ? "Create" : "Edit"} Membership Expiry Reminder
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-end sm:justify-center space-x-3 sm:space-x-[20px] w-full sm:w-auto">
                    <Button
                      type="button"
                      className="w-[100px] text-center flex items-center gap-2 border-primary"
                      variant={"outline"}
                      disabled={isSubmitting}
                      onClick={handleClose}
                    >
                      <i className="fa fa-xmark "></i>
                      Cancel
                    </Button>
                    <LoadingButton
                      type="submit"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      className="w-[110px] cursor-pointer bg-primary hover:bg-primary flex items-center gap-1"
                    >
                      {!isSubmitting &&
                        <i className="fa-regular fa-floppy-disk text-base px-1"></i>
                      }
                      {action == "add" ? "Save" : "Update"}
                    </LoadingButton>
                  </div>
                </div>
              </SheetTitle>
            </SheetHeader>
            <Separator className=" h-[1px] rounded-full     " />

            <SheetDescription className=" h-full  p-6  ">
              <Card className="w-full ">
                <CardHeader>
                  <CardTitle className="text-base font-medium">
                    Details
                  </CardTitle>
                  <p>Trigger: <span className="capitalize">{watcher.trigger_name}</span> <span className="font-medium">(Before the contract expiry)</span></p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex gap-2 items-start bg-yellow-50 p-4 rounded-sm text-yellow-800">
                    <Info className="h-5 w-5 mt-1" />
                    <div className="space-y-2">
                      <p className="font-medium text-sm">Available placeholders for your message:</p>
                      <div className="flex flex-wrap gap-2">
                        {watcher.replaceable?.split(',').map((placeholder) => (
                          <span
                            key={placeholder}
                            className="bg-yellow-100 px-2 py-1 rounded-md text-xs font-medium hover:bg-yellow-200 transition-colors"
                            title="Click to copy"
                            onClick={async () => {
                              const success = await copyToClipboard(`{{${placeholder}}}`);
                              toast({
                                variant: success ? "success" : "destructive",
                                title: success ? "Copied" : "Copy failed",
                                duration: 2000,
                              });
                            }}

                            style={{ cursor: 'pointer' }}
                          >
                            {placeholder}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-yellow-700 mt-2">Click on a placeholder to copy to clipboard</p>
                    </div>
                  </div>
                  <div className="grid gap-6 sm:grid-cols-4">
                    {/* Reminder Name */}
                    <FloatingLabelInput
                      label="Reminder Name"
                      text="*"
                      id="trigger_name"
                      value={watcher.trigger_name}
                      {...register("trigger_name", {
                        required: "Required",
                        maxLength: {
                          value: 50,
                          message: "Maximum 50 characters allowed"
                        }
                      })}
                      error={errors.trigger_name?.message}
                      className="h-10 placeholder:text-zinc-400"
                    />

                    <FloatingLabelInput
                      label="Days"
                      text="*"
                      type="number"
                      min={1}
                      max={10}
                      step={1}
                      id="days"
                      {...register("no_of_days_before", {
                        required: "Required",
                        min: {
                          value: 1,
                          message: "Minimum 1 day"
                        },
                        max: {
                          value: 10,
                          message: "Maximum 10 days"
                        }

                      })
                      }
                      error={errors.no_of_days_before?.message}
                      value={watcher.no_of_days_before}
                    />


                    {/* Status */}
                    <div className="flex items-center justify-start gap-2">
                      <Label htmlFor="status" className="text-sm text-zinc-500">
                        Status
                      </Label>
                      <Switch
                        id="is_active"
                        checked={watch("is_active")}
                        onCheckedChange={(checked) => setValue("is_active", checked)}
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <label
                          htmlFor="emailSubject"
                          className="text-sm text-zinc-500"
                        >
                          Email Subject
                        </label>
                        <Input
                          id="email_subject"
                          value={watcher.email_subject}
                          type="text"
                          {...register("email_subject", {
                            required: "Required",
                            maxLength: {
                              value: 150,
                              message: "Maximum 150 characters allowed"
                            },
                            validate: (value) => {
                              const trimmed = value.trim();
                              const isEmail = /^[\w.-]+@[\w.-]+\.\w{2,}$/i.test(trimmed);
                              return isEmail ? "Subject must not be an email address" : true;
                            },
                          })}
                          placeholder="Enter Subject"
                          className="h-10 placeholder:text-zinc-400"
                        />
                        {errors.email_subject?.message && (
                          <span className="text-red-500 text-xs mt-[5px]">
                            {errors.email_subject?.message}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label
                        htmlFor="emailBody"
                        className="text-sm text-zinc-500"
                      >
                        Email Body
                      </label>
                      <div className="space-y-2">
                        <TooltipProvider>
                          <MinimalTiptapEditor
                            value={textEditorValue}
                            onChange={(value) => {
                              setTextEditoralue(value);
                              setValue("email_body", value as string);
                            }}
                            className="w-full"
                            editorContentClassName="p-5"
                            output="html"
                            placeholder="Type your description here..."
                            autofocus={true}
                            editable={true}
                            editorClassName="focus:outline-none"
                          />
                        </TooltipProvider>
                      </div>
                    </div>
                  </div>

                </CardContent>
              </Card>
            </SheetDescription>
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default MembershipExpiryForm;
